import {
  InternalUserListPaginatedResponseDto,
  InternalUserListPaginatedResponseDtoWithInvitationCount,
  InternalUserListResponseDto,
} from "@/lib/api/interfaces/internal.interface";
import { PaginatedInfo } from "@/lib/api/interfaces/utils";
import {
  useUsersInternalFind,
  useUsersInternalFindInitial,
  UseUsersInternalFindInitialParams,
  UseUsersInternalFindParams,
} from "@/lib/api/requests/internal.requests";

import { formatDate } from "@/lib/utils/date-formatter";
import { Badge } from "@/ui-components/ui/badge";
import { Button } from "@/ui-components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { SuspenseWrapper } from "@/ui-components/ui/suspense-wrapper";
import {
  DataTable2,
  DataTable2ColumnHeader,
  DataTable2Pagination,
  DataTable2Toolbar,
  getDataTable2SelectionColumn,
  useDataTable2,
} from "@/util-components/data-table-2/components";
import { PageHeader, PageLayout, PageTitle } from "@/util-components/page-layout/page-layout";
import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import CreateInternalInvitationDialog from "./create-dialog";
import UserDetailsDialog from "./user-details-dialog";
import { Plus, Users } from "lucide-react";

const InternalUsersWrapper = () => {
  return (
    <PageLayout basePath="/users/internal" className="bg-white">
      <InternalUsers />
    </PageLayout>
  );
};

const getColumns = (): ColumnDef<InternalUserListResponseDto>[] => {
  return [
    getDataTable2SelectionColumn<InternalUserListResponseDto>(),
    {
      id: "name",
      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
      meta: { label: "Name" },
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Name" />,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div>
            <p>
              {row.original.firstName} {row.original.lastName}
            </p>
            <p className="text-sm text-gray-500">{row.original.mail}</p>
          </div>
          {row.original.ownUser ? <Badge>Du</Badge> : null}
        </div>
      ),
    },
    {
      meta: { label: "Letzter Login" },
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Letzter Login" />,
      accessorKey: "lastLogin",
      cell: ({ row }) => formatDate(row.original.lastLogin),
      sortingFn: (a, b, id) => {
        const aValue = a.getValue(id);
        const bValue = b.getValue(id);
        const aTime = aValue ? new Date(aValue as any).getTime() : 0;
        const bTime = bValue ? new Date(bValue as any).getTime() : 0;
        return aTime - bTime;
      },
    },
    {
      meta: { label: "Status" },
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Status" />,
      accessorKey: "isArchive",
      cell: ({ row }) => (row.original.isArchive ? <Badge>Archiviert</Badge> : <Badge>Aktiv</Badge>),
    },
    {
      meta: { label: "Rolle" },
      header: ({ column }) => <DataTable2ColumnHeader column={column} title="Rolle" />,
      accessorKey: "employeeRole",
      cell: ({ row }) => <Badge>{row.original.employeeRole}</Badge>,
    },
    {
      header: "",
      accessorKey: "actions",
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        return (
          <div>
            <Button
              variant="outline"
              onClick={(e) => e.stopPropagation()}
              disabled={!row.original.isArchiveable}
              className="h-9 rounded-xl bg-white border-gray-200 hover:bg-gray-50"
            >
              Archivieren
            </Button>
          </div>
        );
      },
    },
  ];
};

const InternalUsers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isInitial, setIsInitial] = useState(true);
  const [shouldReload, setShouldReload] = useState<{ shouldReload: boolean; refetch: boolean }>({
    shouldReload: false,
    refetch: false,
  });
  const [users, setUsers] = useState<InternalUserListResponseDto[]>();
  const [paginatedInfo, setPaginatedInfo] = useState<PaginatedInfo>();
  const [invitationCount, setInvitationCount] = useState<number>(0);
  const initialUiTextSearch = searchParams.get("textSearch") ?? "";
  const [uiTextSearch, setUiTextSearch] = useState<string>(initialUiTextSearch);
  const initialPage = Number.parseInt(searchParams.get("page") ?? "1", 10);
  const [params, setParams] = useState<UseUsersInternalFindParams>({
    limit: 10,
    page: Number.isFinite(initialPage) && initialPage > 0 ? initialPage : 1,
    ...(initialUiTextSearch.trim().length >= 4 ? { textSearch: initialUiTextSearch.trim() } : {}),
  });
  const [detailsPopupOpen, setDetailsPopupOpen] = useState<{
    open: boolean;
    user: InternalUserListResponseDto | null;
  }>({
    open: false,
    user: null,
  });
  const [createInvitationDialogOpen, setCreateInvitationDialogOpen] = useState(false);

  const onDataLoadedInitial = (data: InternalUserListPaginatedResponseDtoWithInvitationCount) => {
    if (Array.isArray(data?.users?.docs)) {
      setUsers(data.users.docs);
      setPaginatedInfo(data.users);
      setInvitationCount(data.invitationCount);
      setIsInitial(false);
      return;
    }
    setUsers([]);
    setPaginatedInfo(undefined);
    setInvitationCount(0);
    setIsInitial(false);
  };

  const onDataLoaded = (data: InternalUserListPaginatedResponseDto) => {
    if (Array.isArray(data?.docs)) {
      setUsers(data.docs);
      setPaginatedInfo(data);
    }
    setShouldReload({ shouldReload: false, refetch: false });
  };

  useEffect(() => {
    if (!isInitial) {
      setShouldReload({ shouldReload: true, refetch: false });
    }
    setSearchParams(
      {
        page: (params.page ?? 1).toString(),
        ...(params.textSearch ? { textSearch: params.textSearch } : {}),
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const columns = getColumns();

  const { table } = useDataTable2({
    data: users || [],
    columns,
    enableRowSelection: true,
    getRowId: (row) => row._id,
  });

  const selectedCount = table.getSelectedRowModel().rows.length;
  const rowCount = table.getRowModel().rows.length;

  return (
    <div className="h-full flex flex-col">
      <PageHeader>
        <PageTitle title="Nutzer" icon={Users} iconColor="text-blue-600" />
      </PageHeader>
      
      <div className="flex-1 overflow-y-auto w-full">
        <div className="max-w-7xl mx-auto space-y-6">
          <SuspenseWrapper type="green" text="Lädt Nutzer...">
            {isInitial && <InternalUsersInitial onDataLoaded={onDataLoadedInitial} params={params} />}
          </SuspenseWrapper>
          <SuspenseWrapper type="green" text="Lädt Nutzer...">
            {!isInitial && shouldReload.shouldReload && (
              <InternalUsersFind onDataLoaded={onDataLoaded} params={params} shouldRefetch={shouldReload.refetch} />
            )}
          </SuspenseWrapper>

          <div className="bg-white overflow-hidden">
            <DataTable2Toolbar
              className="px-6"
              table={table}
              searchQuery={uiTextSearch}
              onSearchChange={(text) => {
                setUiTextSearch(text);
                const normalized = text.trim();
                if (normalized.length === 0) {
                  setParams({ ...params, textSearch: undefined, page: 1 });
                  return;
                }
                if (normalized.length >= 4) {
                  setParams({ ...params, textSearch: normalized, page: 1 });
                }
              }}
              searchHint={
                uiTextSearch.trim().length > 0 && uiTextSearch.trim().length < 4 ? "Mindestens 4 Zeichen" : undefined
              }
              columnTogglePosition="left"
              left={
                <Select defaultValue="all">
                  <SelectTrigger className="h-10 w-[180px] rounded-xl bg-white border-gray-200">
                    <SelectValue placeholder="Auswahl" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Nutzer</SelectItem>
                  </SelectContent>
                </Select>
              }
              right={
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Link to="/users/invitations">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 rounded-xl bg-white border-gray-200 hover:bg-gray-50"
                    >
                      Einladungen ({invitationCount || 0})
                    </Button>
                  </Link>

                  <Button size="sm" className="h-10 rounded-xl" onClick={() => setCreateInvitationDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Nutzer einladen
                  </Button>
                </div>
              }
            />

            <div className="px-6 pb-4 pt-4">
              <DataTable2
                table={table}
                onRowClick={(row) => setDetailsPopupOpen({ open: true, user: row })}
                emptyTitle="Keine Nutzer gefunden"
                emptyDescription="Passe deine Suche an oder versuche es später erneut."
              />
            </div>

            <div className="border-t border-gray-100 px-6 py-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="text-sm text-gray-500">
                  {selectedCount > 0 ? `${selectedCount} von ${rowCount} ausgewählt.` : null}
                </div>
                {paginatedInfo ? (
                  <DataTable2Pagination
                    currentPage={params.page ?? 1}
                    pageCount={paginatedInfo.pageCount ?? 0}
                    hasNextPage={paginatedInfo.hasNextPage ?? false}
                    hasPrevPage={paginatedInfo.hasPrevPage ?? false}
                    onPageChange={(page) => setParams({ ...params, page })}
                    pageSize={params.limit ?? 10}
                    onPageSizeChange={(limit) => setParams({ ...params, limit, page: 1 })}
                  />
                ) : null}
              </div>
            </div>
          </div>

          <SuspenseWrapper type="green" text="Öffnet Dialog...">
            {detailsPopupOpen.open && detailsPopupOpen.user && (
              <UserDetailsDialog
                open={detailsPopupOpen.open}
                onClose={(refetchData) => {
                  setDetailsPopupOpen({ open: false, user: null });
                  if (refetchData) {
                    setShouldReload({ shouldReload: true, refetch: true });
                  }
                }}
                userId={detailsPopupOpen.user._id}
              />
            )}
          </SuspenseWrapper>

          <SuspenseWrapper type="green" text="Öffnet Dialog...">
            {createInvitationDialogOpen && (
              <CreateInternalInvitationDialog
                open={createInvitationDialogOpen}
                onClose={(refetchData) => {
                  setCreateInvitationDialogOpen(false);
                  if (refetchData) {
                    setShouldReload({ shouldReload: true, refetch: true });
                  }
                }}
              />
            )}
          </SuspenseWrapper>
        </div>
      </div>
    </div>
  );
};

const InternalUsersInitial = ({
  onDataLoaded,
  params,
}: {
  onDataLoaded: (data: InternalUserListPaginatedResponseDtoWithInvitationCount) => void;
  params: UseUsersInternalFindInitialParams;
}) => {
  const { data: response } = useUsersInternalFindInitial(params);

  useEffect(() => {
    if (response) {
      onDataLoaded(response);
    }
  }, [response, onDataLoaded]);

  return null;
};

const InternalUsersFind = ({
  onDataLoaded,
  params,
  shouldRefetch,
}: {
  onDataLoaded: (data: InternalUserListPaginatedResponseDto) => void;
  params: UseUsersInternalFindParams;
  shouldRefetch: boolean;
}) => {
  const { data: response, refetch } = useUsersInternalFind(params);

  const refetchData = useCallback(async () => {
    const { data } = await refetch();
    if (data) {
      onDataLoaded(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch]);

  useEffect(() => {
    if (response) {
      onDataLoaded(response);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, onDataLoaded]);

  useEffect(() => {
    if (shouldRefetch) {
      refetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefetch]);

  return null;
};

export default InternalUsersWrapper;
